"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TarotCardBackground } from "./Background/index";
import { useUser } from "@/contexts/UserContext";
import { tarotDeck } from "@/lib/data/tarotDeck";
import { FeatheredBlurBackdrop } from "./FeatheredBlurBackdrop";
import ProductHuntBadge from "./ProductHuntBadge";
import { useBreakpoint } from "@/hooks/useBreakpoint";
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    scale: 0.65
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 2,
      delay: 1,
      ease: [0.6, -0.05, 0.01, 0.99],
    },
  },
};

export default function HeroSection() {
  const [isLoaded, setIsLoaded] = useState(false);
  const { userProfile } = useUser();
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const { isMobile } = useBreakpoint();

  const getRedirectUrl = () => {
    if (!userProfile) return "/signup";
    return `/${userProfile.handle}/profile?tab=past-readings`;
  };

  return (
    <>
      <div className="relative min-h-screen">
        <AnimatePresence>
          {isLoaded && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="relative z-10 min-h-screen flex items-center justify-center px-2"
            >
              <div className="absolute inset-0 sm:inset-y-48 sm:inset-x-16 sm:rounded-3xl -z-10 overflow-hidden sm:shadow-2xl">
                <video
                  className="absolute w-full h-full object-cover object-center"
                  src="/hero.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls={false}
                />
              </div>
              
              <div className="absolute inset-0 w-full h-full">
                <TarotCardBackground baseDeck={tarotDeck} />
              </div>
                {/* <FeatheredBlurBackdrop blurAmount="10px" featherSize="150px"> */}
                  <motion.div 
                    initial="hidden"
                    animate="visible"
                    variants={itemVariants}
                    className={`mx-auto relative z-10 p-4 sm:p-6 aspect-[1/1.8] sm:border sm:border-zinc-100 sm:border-8 sm:rounded-3xl flex flex-col justify-between sm:justify-center items-center `}
                    style={!isMobile ? {
                      backgroundImage: "radial-gradient(rgba(10, 50, 100, 0.9), rgba(255, 0, 200, 0.1)), url('/card-bg-big.jpg')",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    } : undefined}
                  >
                    <div className="flex flex-col items-center pt-16 sm:pt-24">
                      <motion.h1
                        variants={itemVariants}
                        className="text-3xl drop-shadow-xl md:text-4xl font-extrabold text-[#0A3264] sm:text-zinc-50 mb-4 sm:mb-2 leading-tighter font-heading text-center tracking-tighter"
                      >
                        Arcana Vibes
                      </motion.h1>

                      <motion.div
                        variants={itemVariants}
                        className="text-primary sm:text-zinc-50 mb-4 sm:mb-8 mt-64 sm:mt-0 max-w-xl text-center "
                      >
                        <p className=" mb-4 sm:mb-6 drop-shadow-2xl text-[#0A3264] sm:text-zinc-50 text-md sm:text-lg max-w-sm mx-auto leading-tight sm:leading-snug">
                          On-demand interactive personalized tarot card readings
                          based on your astrological charts.
                        </p>

                        <Button
                          size="xl"
                          className="bg-white hover:bg-white text-primary hover:text-primary transition-all duration-300 rounded-xl sm:rounded-xl hover:shadow-lg mb-4 sm:mb-6"
                        >
                          <Link href={getRedirectUrl()}>
                            <span className="flex items-center gap-2 text-sm font-subheading">
                              <Sparkle className="h-4 w-4" />
                              {userProfile
                                ? "Go to Your Readings"
                                : "Sign up for a Free Trial"}
                              <Sparkle className="h-4 w-4" />
                            </span>
                          </Link>
                        </Button>

                        <p className="hidden sm:block drop-shadow-2xl text-primary sm:text-zinc-50 text-sm max-w-sm sm:text-md leading-tight sm:leading-snug">
                          Daily Draws, Tarot Card Readings, Natal Chart Interpretations, and more! Everything is saved so you
                          can return to it at any time.
                        </p>
                        <div className="block pt-2 sm:hidden">
                          <p className="hidden sm:block text-sm text-primary sm:text-zinc-50 max-w-sm leading-tight sm:leading-snug">
                            Check out Arcana Vibes on Product Hunt!
                          </p>
                          <div className="pt-2">
                            <ProductHuntBadge />  
                          </div>
                        </div>
                      </motion.div>
                    </div>
                </motion.div>
              {/* </FeatheredBlurBackdrop> */}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
